import { useCallback, useEffect, useState } from "react";
import { useLang } from "@/i18n/LangContext";
import { useAuth } from "@/lib/AuthContext";
import api, { formatApiError } from "@/lib/api";
import TrialBanner from "@/components/TrialBanner";
import {
  BRIEFING_REQUEST_TIMEOUT_MS,
  HOUR_AFTERNOON_START,
  HOUR_EVENING_START,
} from "@/lib/constants";

const DOMAINS = ["logistica", "casa", "profissional", "financas", "educacao"];

export default function Dashboard() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [tab, setTab] = useState("active");
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([api.get("/items"), api.get("/dashboard/summary")]);
    setItems(a.data.items);
    setSummary(b.data);
    setLoading(false);
  }, []);

  const loadBriefing = useCallback(async () => {
    setBriefingLoading(true);
    try {
      const { data } = await api.get("/briefings/today", { timeout: BRIEFING_REQUEST_TIMEOUT_MS });
      setBriefing(data.briefing);
    } finally {
      setBriefingLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadBriefing();
  }, [load, loadBriefing]);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < HOUR_AFTERNOON_START) return t("app.good_morning");
    if (h < HOUR_EVENING_START) return t("app.good_afternoon");
    return t("app.good_evening");
  })();

  const filtered = items.filter((it) => {
    if (tab === "active") return it.status === "in_progress";
    if (tab === "auth") return it.status === "needs_auth";
    if (tab === "done") return it.status === "done";
    return true;
  });

  // Empty-state copy per tab. Replaces a nested ternary in JSX for readability.
  const emptyStateKey = { active: "app.noActive", auth: "app.noAuth", done: "app.noDone" }[tab] || "app.noActive";

  // Briefing copy: derived once, replaces nested ternary in JSX.
  let briefingBlock;
  if (briefing) {
    briefingBlock = (
      <p className="mt-5 text-[17px] leading-relaxed whitespace-pre-line" data-testid="briefing-narrative">
        {briefing.narrative}
      </p>
    );
  } else if (briefingLoading) {
    briefingBlock = (
      <p className="mt-5 font-display-italic text-[var(--ink-soft)]">{t("app.briefingGenerating")}</p>
    );
  } else {
    briefingBlock = <p className="mt-5 font-display-italic text-[var(--ink-soft)]">…</p>;
  }

  const onAuthorise = async (id) => {
    await api.post(`/items/${id}/authorise`);
    load();
  };
  const onComplete = async (id) => {
    await api.post(`/items/${id}/complete`);
    load();
  };

  const firstName = (user?.name || "").split(" ")[0];

  return (
    <div className="px-6 md:px-12 lg:px-16 py-12 md:py-16 max-w-6xl">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="label-cap">— {new Date().toLocaleDateString(lang === "pt" ? "pt-PT" : "en-GB", { weekday: "long", day: "numeric", month: "long" })}</p>
          <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight mt-3" data-testid="dashboard-greeting">
            {greeting}, <span className="font-display-italic">{firstName}</span>.
          </h1>
        </div>
        <button onClick={() => setShowNew(true)} className="btn-outline" data-testid="add-item-btn">
          + {t("app.addItem")}
        </button>
      </div>

      <TrialBanner />

      {/* Briefing card */}
      <section className="mt-10 card-flat" data-testid="briefing-card">
        <div className="flex items-baseline justify-between">
          <p className="label-cap text-[var(--oxblood)]">— {t("app.briefingTitle")}</p>
          <button onClick={loadBriefing} disabled={briefingLoading} className="btn-ghost text-[10px]" data-testid="briefing-refresh-btn">
            {briefingLoading ? t("app.briefingGenerating") : "↻ " + t("app.briefingRefresh")}
          </button>
        </div>
        {briefingBlock}
      </section>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border)] mt-12">
        <Tile label={t("app.total_items")} value={summary?.totals?.in_progress ?? 0} testId="tile-in-progress" />
        <Tile label={t("app.done")} value={summary?.this_week?.done ?? 0} testId="tile-done" />
        <Tile label={t("app.needs_auth")} value={summary?.totals?.needs_auth ?? 0} testId="tile-needs-auth" highlight />
        <Tile label={t("app.hours_saved")} value={`${summary?.this_week?.hours_saved ?? 0}h`} testId="tile-hours" accent />
      </div>

      {/* Tabs */}
      <div className="mt-16 flex items-center gap-px bg-[var(--border)]" data-testid="items-tabs">
        <TabBtn active={tab === "active"} onClick={() => setTab("active")} testId="tab-active">
          {t("app.tabActive")}
          <Badge count={summary?.totals?.in_progress ?? 0} />
        </TabBtn>
        <TabBtn active={tab === "auth"} onClick={() => setTab("auth")} testId="tab-auth">
          {t("app.tabAuth")}
          <Badge count={summary?.totals?.needs_auth ?? 0} accent />
        </TabBtn>
        <TabBtn active={tab === "done"} onClick={() => setTab("done")} testId="tab-done">
          {t("app.tabDone")}
          <Badge count={summary?.this_week?.done ?? 0} />
        </TabBtn>
      </div>

      <div className="mt-8 space-y-px bg-[var(--border)]" data-testid="items-list">
        {loading && <SkeletonRow />}
        {!loading && filtered.length === 0 && (
          <div className="bg-[var(--bg)] py-12 text-center font-display-italic text-[var(--ink-soft)]" data-testid="items-empty">
            {t(emptyStateKey)}
          </div>
        )}
        {filtered.map((it) => (
          <ItemRow
            key={it.id}
            item={it}
            onAuthorise={onAuthorise}
            onComplete={onComplete}
            t={t}
            lang={lang}
          />
        ))}
      </div>

      {showNew && <NewItemDialog onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function Tile({ label, value, accent, highlight, testId }) {
  // Color picked by mode: accent (dark with gold), highlight (oxblood), default (ink-soft).
  let labelColor;
  if (accent) labelColor = "text-[var(--gold)]";
  else if (highlight) labelColor = "text-[var(--oxblood)]";
  else labelColor = "text-[var(--ink-soft)]";
  return (
    <div
      className={`p-8 ${accent ? "bg-[var(--ink)] text-[var(--bg)]" : "bg-[var(--bg)]"}`}
      data-testid={testId}
    >
      <p className={`label-cap ${labelColor}`}>{label}</p>
      <p className={`font-display text-5xl md:text-6xl font-light mt-6 tracking-tight ${accent ? "text-[var(--bg)]" : "text-[var(--ink)]"}`}>
        {value}
      </p>
    </div>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-3 px-6 py-4 text-[12px] tracking-monocle uppercase transition-colors duration-300 ${
        active ? "bg-[var(--bg)] text-[var(--ink)] border-t-2 border-[var(--oxblood)]" : "bg-[var(--surface)] text-[var(--ink-soft)] hover:text-[var(--ink)]"
      }`}
    >
      {children}
    </button>
  );
}

function Badge({ count, accent }) {
  return (
    <span className={`text-[10px] tracking-monocle uppercase px-2 py-0.5 ${accent ? "bg-[var(--oxblood)] text-[var(--bg)]" : "bg-[var(--border)] text-[var(--ink)]"}`}>
      {count}
    </span>
  );
}

function ItemRow({ item, onAuthorise, onComplete, t, lang }) {
  const domainLabel = t(`app.domainLabels.${item.domain}`);
  const created = new Date(item.created_at);
  const timeStr = created.toLocaleDateString(lang === "pt" ? "pt-PT" : "en-GB", { day: "numeric", month: "short" });
  return (
    <div className="bg-[var(--bg)] grid grid-cols-12 gap-4 px-6 py-6 items-center" data-testid={`item-row-${item.id}`}>
      <div className="col-span-12 md:col-span-2">
        <p className="label-cap text-[var(--oxblood)]">{domainLabel}</p>
        <p className="text-xs text-[var(--ink-faint)] mt-1">{timeStr}</p>
      </div>
      <div className="col-span-12 md:col-span-7">
        <p className="font-display text-xl font-light leading-snug">{item.title}</p>
        {item.description && (
          <p className="text-sm text-[var(--ink-soft)] mt-2 leading-relaxed">{item.description}</p>
        )}
      </div>
      <div className="col-span-12 md:col-span-3 flex md:justify-end gap-3">
        {item.status === "needs_auth" && (
          <button className="btn-primary" onClick={() => onAuthorise(item.id)} data-testid={`item-authorise-${item.id}`}>
            {t("app.authorise")}
          </button>
        )}
        {item.status === "in_progress" && (
          <button className="btn-outline" onClick={() => onComplete(item.id)} data-testid={`item-complete-${item.id}`}>
            {t("app.markDone")}
          </button>
        )}
        {item.status === "done" && (
          <span className="label-cap text-[var(--success)]">✓</span>
        )}
      </div>
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="bg-[var(--bg)] p-6 animate-pulse">
      <div className="h-3 bg-[var(--border)] w-24 mb-4" />
      <div className="h-5 bg-[var(--border)] w-2/3" />
    </div>
  );
}

function NewItemDialog({ onClose, onCreated }) {
  const { t } = useLang();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [domain, setDomain] = useState("logistica");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/items", { title, description, domain });
      onCreated();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-[var(--ink)]/40 flex items-center justify-center px-4" onClick={onClose} data-testid="new-item-dialog">
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-[var(--surface)] border border-[var(--border)] p-10 w-full max-w-lg"
      >
        <p className="label-cap">— {t("app.newItem")}</p>
        <h2 className="font-display text-3xl font-light tracking-tight mt-4">{t("app.newItemTitle")}</h2>
        <div className="mt-8 space-y-6">
          <label className="block">
            <span className="label-cap">{t("app.title")}</span>
            <input className="line-input" required value={title} onChange={(e) => setTitle(e.target.value)} data-testid="new-item-title" />
          </label>
          <label className="block">
            <span className="label-cap">{t("app.details")}</span>
            <input className="line-input" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="new-item-details" />
          </label>
          <div>
            <p className="label-cap mb-2">{t("app.domain")}</p>
            <div className="flex flex-wrap gap-2">
              {DOMAINS.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDomain(d)}
                  data-testid={`new-item-domain-${d}`}
                  className={`px-4 py-2 text-[11px] tracking-monocle uppercase border ${
                    domain === d
                      ? "bg-[var(--ink)] text-[var(--bg)] border-[var(--ink)]"
                      : "border-[var(--border)] text-[var(--ink-soft)] hover:border-[var(--ink)] hover:text-[var(--ink)]"
                  }`}
                >
                  {t(`app.domainLabels.${d}`)}
                </button>
              ))}
            </div>
          </div>
        </div>
        {error && <p className="text-[var(--error)] text-sm mt-4">{error}</p>}
        <div className="flex justify-end gap-4 mt-10">
          <button type="button" onClick={onClose} className="btn-ghost" data-testid="new-item-cancel">
            {t("app.cancel")}
          </button>
          <button type="submit" disabled={submitting} className="btn-primary" data-testid="new-item-create">
            {submitting ? "…" : t("app.create")}
          </button>
        </div>
      </form>
    </div>
  );
}
